import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import type { TFunction } from 'hooks/use-translation';
import TextButton from 'src/components/core/button/text-button';

import { useSelfServeVerifyButton } from './use-self-serve-verify-button';
import type { SelfServeBannerError, SelfServeCtaState } from './use-self-serve-verify-button';

/**
 * FINTECH-4845. Net-new, inline "Verify" control rendered as the Mobile Phone field's
 * `endAdornment` -- the SAME slot the verified badge occupies once the number is confirmed (see
 * `my-account.jsx`). Deliberately separate from the pre-existing "Verify Now" banner
 * (`phone-verification-cta/phone-verification-cta.tsx`), which this component neither renders
 * nor depends on. `my-account.jsx` suppresses the banner while this flag is on, so the two
 * verify affordances never show simultaneously for the same unverified number.
 */
export function SelfServeVerifyButton(): JSX.Element | null {
  const { t } = useTranslation();
  const { isVisible, isSending, cta, bannerError, onVerifyClick } = useSelfServeVerifyButton();

  if (!isVisible) {
    return null;
  }

  if (bannerError) {
    // Dutchie Reviewer finding (non-blocking, addressed): `title` alone is unreachable on touch
    // devices (no hover) -- `aria-label` carries the FULL message as the accessible name so
    // assistive tech always gets it, while the short label stays the visible text for sighted
    // users regardless of input method.
    const message = errorMessage(bannerError, t);
    return (
      <ErrorText role='alert' title={message} aria-label={message}>
        {errorShortLabel(bannerError, t)}
      </ErrorText>
    );
  }

  if (cta?.kind === 'verified_other') {
    // Adversarial-review finding (addressed): `aria-label` alone doesn't reliably contribute an
    // accessible name on a plain `<span>` -- its implicit `generic` role's naming is author-
    // prohibited per the accname spec, so a screen reader can silently ignore it. `role='note'`
    // is a static, non-interactive role that DOES permit an author-supplied name.
    const message = verifiedOtherMessage(cta, t);
    return (
      <InfoText role='note' title={message} aria-label={message}>
        {t('account.phoneVerification.verifiedOtherShort', 'Not this account')}
      </InfoText>
    );
  }

  if (cta?.kind !== 'leg_one') {
    return null;
  }

  const label = t('account.phoneVerification.cta', 'Verify');

  return (
    <VerifyButton
      type='button'
      aria-label={t('account.phoneVerification.title', 'Verify your phone number')}
      onClick={() => void onVerifyClick()}
      disabled={isSending}
    >
      {isSending ? t('account.phoneVerification.verifying', 'Verifying…') : label}
    </VerifyButton>
  );
}

function verifiedOtherMessage(cta: Extract<SelfServeCtaState, { kind: 'verified_other' }>, t: TFunction): string {
  return cta.ownerEmailHint
    ? t('account.phoneVerification.conflictWithHint', {
        defaultValue:
          'This number belongs to {{ownerEmailHint}}. Please log in with that account or change your number below.',
        ownerEmailHint: cta.ownerEmailHint,
      })
    : t(
        'account.phoneVerification.conflict',
        'This number belongs to another account. Please log in with that account or change your number below.'
      );
}

function errorShortLabel(bannerError: SelfServeBannerError, t: TFunction): string {
  switch (bannerError.kind) {
    case 'rate_limited':
      return t('account.phoneVerification.rateLimitedShort', 'Too many attempts');
    case 'invalid_number':
      return t('account.phoneVerification.invalidNumberShort', "Can't send code");
    case 'unavailable':
    default:
      return t('account.phoneVerification.unavailableShort', 'Unavailable');
  }
}

function errorMessage(bannerError: SelfServeBannerError, t: TFunction): string {
  switch (bannerError.kind) {
    case 'rate_limited':
      return t('account.phoneVerification.rateLimited', "You've made too many attempts. Please try again later.");
    case 'invalid_number':
      return t(
        'account.phoneVerification.invalidNumber',
        "We can't send a code to the number on file. Please update it below."
      );
    case 'unavailable':
    default:
      return t('account.phoneVerification.unavailable', 'Phone verification is unavailable right now.');
  }
}

const VerifyButton = styled(TextButton)`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 0;
  white-space: nowrap;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const InfoText = styled.span`
  color: var(--dcm-phone-verification-inline-warning-color, #9a7b00);
  cursor: help;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-decoration: underline dotted;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ErrorText = styled.span`
  color: var(--dcm-phone-verification-inline-error-color, #b3261e);
  cursor: help;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-decoration: underline dotted;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
